import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { TrajectoryCanvas } from '../../components/TrajectoryCanvas';
import { PrintButton } from '../../components/PrintButton';
import { ArrowLeft, Compass, PencilLine } from 'lucide-react';
import { SafeVideoEmbed } from '../../components/SafeVideoEmbed';

/* ----------------------------- Schémas SVG ----------------------------- */

// Couleurs neutres compatibles dark mode (utilise currentColor + opacités)
const ASPHALT = '#3a3f4a';
const LANE = '#fff';
const BG = 'transparent';
const IDEAL_GREEN = '#16a34a';

const PointBadge = ({ cx, cy, label, color }) => (
  <g>
    <circle cx={cx} cy={cy} r={11} fill={color} stroke="white" strokeWidth={2} />
    <text x={cx} y={cy + 4} textAnchor="middle" fontSize="12" fontWeight="900" fill="white">{label}</text>
  </g>
);

const MotoIcon = ({ x, y }) => (
  <g transform={`translate(${x},${y})`}>
    <rect x={-9} y={-14} width={18} height={28} rx={4} fill="#cf2031" />
    <circle cx={0} cy={-12} r={5} fill="#1a1a1a" />
    <circle cx={0} cy={12} r={5} fill="#1a1a1a" />
  </g>
);

/**
 * Overlay SVG pour la trajectoire idéale (verte).
 * - Trace pointillé épais
 * - Animation `draw-in` via CSS (stroke-dashoffset)
 * - Flèche directionnelle au bout du chemin
 */
const IdealOverlay = ({ d }) => (
  <svg viewBox="0 0 600 360" className="w-full h-full" style={{ background: BG }} aria-hidden="true">
    <defs>
      <marker
        id="c2r-arrow-green"
        viewBox="0 0 10 10"
        refX="6"
        refY="5"
        markerWidth="5"
        markerHeight="5"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={IDEAL_GREEN} />
      </marker>
      <filter id="c2r-glow-green" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="2.5" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    {/* Halo vert flou en dessous */}
    <path
      d={d}
      stroke={IDEAL_GREEN}
      strokeWidth={11}
      strokeOpacity={0.20}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="c2r-ideal-path c2r-ideal-path--halo"
    />
    {/* Trait principal pointillé qui s'anime */}
    <path
      d={d}
      stroke={IDEAL_GREEN}
      strokeWidth={5}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="10 8"
      markerEnd="url(#c2r-arrow-green)"
      filter="url(#c2r-glow-green)"
      className="c2r-ideal-path"
    />
  </svg>
);

// 1. Virage en S (gauche puis droite)
const SvgVirageS = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" style={{ background: BG }} aria-hidden="true">
    {/* route */}
    <path d="M 230 350 C 230 280, 380 240, 380 180 S 230 110, 380 30" stroke={ASPHALT} strokeWidth={90} fill="none" strokeLinecap="round" />
    {/* axe central pointillé */}
    <path d="M 230 350 C 230 280, 380 240, 380 180 S 230 110, 380 30" stroke={LANE} strokeWidth={2} fill="none" strokeDasharray="10 8" />
    {/* moto en bas */}
    <MotoIcon x={230} y={335} />
    {/* points clés */}
    <PointBadge cx={245} cy={290} label="A" color="#16a34a" />
    <PointBadge cx={310} cy={235} label="B" color="#1d4ed8" />
    <PointBadge cx={365} cy={170} label="C" color="#f59e0b" />
    <PointBadge cx={310} cy={90} label="D" color="#7c3aed" />
  </svg>
);

// 2. Virage en épingle
const SvgEpingle = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" style={{ background: BG }} aria-hidden="true">
    {/* montée gauche puis demi-tour */}
    <path d="M 200 350 L 200 150 A 110 110 0 0 1 420 150 L 420 350" stroke={ASPHALT} strokeWidth={90} fill="none" />
    <path d="M 200 350 L 200 150 A 110 110 0 0 1 420 150 L 420 350" stroke={LANE} strokeWidth={2} fill="none" strokeDasharray="10 8" />
    <MotoIcon x={200} y={335} />
    <PointBadge cx={210} cy={300} label="A" color="#16a34a" />
    <PointBadge cx={210} cy={170} label="B" color="#1d4ed8" />
    <PointBadge cx={310} cy={50} label="C" color="#f59e0b" />
    <PointBadge cx={420} cy={170} label="D" color="#7c3aed" />
  </svg>
);

// Petit badge numéroté pour les sorties (1, 2, 3)
const ExitBadge = ({ cx, cy, label }) => (
  <g>
    <rect x={cx - 11} y={cy - 11} width={22} height={22} rx={5} fill="#0f1116" stroke="white" strokeWidth={2} />
    <text x={cx} y={cy + 4} textAnchor="middle" fontSize="13" fontWeight="900" fill="white">{label}</text>
  </g>
);

// 3. Carrefour à sens giratoire — schéma simplifié avec sorties numérotées (1, 2, 3)
//    et waypoints A, B, C, D placés sur la ligne discontinue (cardinaux : sud, est, nord, ouest).
//    Ce schéma est partagé entre la situation 3 (1ère sortie) et la situation 4 (3ème / demi-tour).
const SvgGiratoire = () => (
  <svg viewBox="0 0 600 360" className="w-full h-full" style={{ background: BG }} aria-hidden="true">
    {/* anneau */}
    <circle cx={300} cy={180} r={120} fill="none" stroke={ASPHALT} strokeWidth={70} />
    <circle cx={300} cy={180} r={120} fill="none" stroke={LANE} strokeWidth={2} strokeDasharray="10 8" />
    {/* branches */}
    <rect x={270} y={300} width={60} height={60} fill={ASPHALT} />
    <rect x={270} y={0} width={60} height={60} fill={ASPHALT} />
    <rect x={460} y={150} width={140} height={60} fill={ASPHALT} />
    <rect x={0} y={150} width={140} height={60} fill={ASPHALT} />
    {/* central island avec verdure */}
    <circle cx={300} cy={180} r={60} fill="#1f3a1f" />
    <circle cx={290} cy={170} r={14} fill="#3b8a3b" />
    <circle cx={315} cy={195} r={12} fill="#3b8a3b" />
    <circle cx={285} cy={200} r={10} fill="#3b8a3b" />
    {/* moto à l'entrée (sud) */}
    <MotoIcon x={300} y={330} />
    {/* numéros des sorties (sens CCW depuis le sud) */}
    <ExitBadge cx={555} cy={140} label="1" />
    <ExitBadge cx={345} cy={25} label="2" />
    <ExitBadge cx={45} cy={140} label="3" />
    {/* waypoints A, B, C, D sur la ligne discontinue (cardinaux) */}
    <PointBadge cx={300} cy={300} label="A" color="#16a34a" />
    <PointBadge cx={420} cy={180} label="B" color="#f59e0b" />
    <PointBadge cx={300} cy={60} label="C" color="#1d4ed8" />
    <PointBadge cx={180} cy={180} label="D" color="#7c3aed" />
  </svg>
);

const SITUATIONS = [
  {
    id: 'virage-s',
    title: 'Virage en S — à gauche puis à droite',
    points: [
      { tag: 'A', label: 'Approche', detail: 'Adapter sa vitesse et se placer.', color: '#16a34a' },
      { tag: 'B', label: 'Entrée de virage', detail: 'Se placer en entrée de virage.', color: '#1d4ed8' },
      { tag: 'C', label: 'Point de corde', detail: 'Passer par le point de corde.', color: '#f59e0b' },
      { tag: 'D', label: 'Sortie de virage', detail: 'Se redresser et accélérer progressivement.', color: '#7c3aed' },
    ],
    keys: ['Approche : adapter sa vitesse et se placer', 'Entrée : se placer en entrée de virage', 'Point de corde : viser le point de corde', 'Sortie : redresser la moto et accélérer progressivement'],
    Schema: SvgVirageS,
    /* Trajectoire de sécurité — RESTE TOUJOURS À DROITE de la ligne discontinue.
       Décale d'environ +22px (mi-largeur de voie) sur l'axe X par rapport au tracé central
       de la chaussée `M 230 350 C 230 280, 380 240, 380 180 S 230 110, 380 30`. */
    idealPath: 'M 252 340 C 252 280, 402 240, 402 180 S 252 110, 402 30 L 402 5',
  },
  {
    id: 'epingle',
    title: 'Virage en épingle — à droite',
    points: [
      { tag: 'A', label: 'Approche', detail: 'Adapter sa vitesse et se placer.', color: '#16a34a' },
      { tag: 'B', label: 'Entrée', detail: "Se placer large à l'entrée.", color: '#1d4ed8' },
      { tag: 'C', label: 'Point de corde (apex)', detail: 'Viser le point de corde.', color: '#f59e0b' },
      { tag: 'D', label: 'Sortie', detail: 'Redresser et accélérer progressivement.', color: '#7c3aed' },
    ],
    keys: ['Approche : adapter sa vitesse et se placer', "Entrée : se placer large à l'entrée", 'Point de corde : viser le point de corde (apex)', 'Sortie : redresser et accélérer progressivement'],
    Schema: SvgEpingle,
    /* Trajectoire de sécurité — épingle DROITE, le rider reste sur le côté DROIT de la chaussée.
       Sur la branche montante (gauche), c'est x=222 (intérieur droit du tracé moitié-droite).
       Dans le virage, c'est l'arc INTÉRIEUR (radius 88 au lieu de 110).
       Sur la branche descendante (droite), c'est x=398. JAMAIS au-delà du milieu. */
    idealPath: 'M 222 345 L 222 150 A 88 88 0 0 1 398 150 L 398 345',
  },
  {
    id: 'giratoire',
    title: 'Carrefour à sens giratoire — 1ère ou 2ème sortie',
    points: [
      { tag: 'A', label: 'Entrée (sud)', detail: 'Cédez le passage à gauche, insérez-vous en sens anti-horaire.', color: '#16a34a' },
      { tag: 'B', label: '1ère sortie (est)', detail: 'Sortie n°1 — on reste à DROITE/extérieur, on sort ici.', color: '#f59e0b' },
      { tag: 'C', label: '2ème sortie (nord)', detail: 'Sortie n°2 — on resterait à DROITE/extérieur jusqu\'ici.', color: '#1d4ed8' },
      { tag: 'D', label: '3ème sortie (ouest)', detail: 'Sortie n°3 — voir la situation suivante (technique différente).', color: '#7c3aed' },
    ],
    keys: [
      'Approche : adapter sa vitesse, se placer à droite et lire la signalisation',
      'Entrée (A) : céder le passage à gauche, s’insérer en sens anti-horaire',
      '1ère sortie (B / n°1) ou 2ème sortie (C / n°2) → rester à DROITE/extérieur de l’anneau',
      'Pour la 3ème sortie ou un demi-tour → voir la situation suivante',
      'Sortie : clignotant droit, contrôle d’angle mort, sortir en sécurité',
    ],
    Schema: SvgGiratoire,
    /* Trajectoire de sécurité — 1ère sortie (SUD → EST = sortie B/n°1), sens ANTI-HORAIRE visuel.
       On reste sur l'arc EXTERIEUR du giratoire (rayon 140 centré (300,180), entre la ligne
       discontinue r=120 et le bord extérieur r=155).
       Endpoints exactement sur le cercle r=140 :
         - Sud du cercle : (300, 320)
         - Est du cercle : (440, 180)
       Avec large-arc=0 et sweep=0, SVG trace l'arc court (90°) qui passe par le SUD-EST
       (côté droit de l'anneau). On termine sur la voie de droite de la branche est (y=195). */
    idealPath: 'M 315 355 L 315 325 Q 308 322 300 320 A 140 140 0 0 0 440 180 L 590 195',
  },
  {
    id: 'giratoire-3eme',
    title: 'Carrefour à sens giratoire — 3ème sortie ou demi-tour',
    points: [
      { tag: 'A', label: 'Entrée (sud)', detail: 'Cédez le passage à gauche, insérez-vous à GAUCHE/intérieur.', color: '#16a34a' },
      { tag: 'B', label: '1ère sortie (est)', detail: 'Sortie n°1 dépassée — on reste à GAUCHE/intérieur.', color: '#f59e0b' },
      { tag: 'C', label: '2ème sortie (nord)', detail: 'Sortie n°2 dépassée — on commence à se rabattre à DROITE.', color: '#1d4ed8' },
      { tag: 'D', label: '3ème sortie (ouest)', detail: 'Sortie n°3 — clignotant droit, contrôle d\'angle mort, on sort.', color: '#7c3aed' },
    ],
    keys: [
      'Approche : adapter sa vitesse, se placer à droite et lire la signalisation',
      'Entrée (A) : céder le passage à gauche, s’insérer à GAUCHE/intérieur',
      'Circulation intérieure : on dépasse B (n°1) puis C (n°2) en restant à gauche',
      'Après C : on se RABAT à DROITE/extérieur (clignotant droit)',
      'Sortie (D / n°3) : contrôle d’angle mort, on sort en sécurité',
      'Demi-tour (retour vers le sud) : même technique, on continue jusqu\'à la sortie suivante',
    ],
    Schema: SvgGiratoire,
    /* Trajectoire 3ème sortie / demi-tour — INSIDE puis OUTSIDE après la 2ème sortie.
       Sens ANTI-HORAIRE (CCW visuel = sweep=0).
         1) Branche sud, voie droite → entrée à l'INTÉRIEUR (rayon ~100)
         2) Inside arc CCW : sud → est (B/n°1) → nord (C/n°2), 180° = passe les 2 premières sorties
         3) Après C : transition diagonale INSIDE → OUTSIDE
         4) Outside arc CCW : NW → ouest (D/n°3), ~45-90°
         5) Sortie ouest, voie de droite (y=195) */
    idealPath: 'M 315 350 L 315 285 A 100 100 0 1 0 300 80 C 285 70, 240 75, 215 110 A 140 140 0 0 0 165 195 L 0 195',
  },
];

export default function Trajectoires() {
  const navigate = useNavigate();

  return (
    <div className="space-y-5" data-testid="trajectoires-page">
      <div className="flex items-center justify-between gap-2 print:hidden">
        <Button onClick={() => navigate('/epreuves')} variant="ghost" size="sm" className="-ml-2" data-testid="trajectoires-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux épreuves
        </Button>
        <PrintButton label="Imprimer les schémas" testid="trajectoires-print-btn" />
      </div>

      <section className="mc-card relative overflow-hidden p-5 sm:p-7" data-testid="trajectoires-hero">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1 inline-flex items-center gap-1">
            <Compass className="w-3 h-3" /> Trajectoires de sécurité
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Trace ta trajectoire dans chaque situation.</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">
            Trois situations courantes à l'examen : <strong>virage en S</strong>, <strong>épingle</strong> et <strong>carrefour à sens giratoire</strong>.
            Dessine la trajectoire de sécurité que tu adopterais, puis <strong className="text-[#16a34a]">valide ton tracé pour révéler la correction en vert</strong>.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold text-foreground/70">
            <PencilLine className="w-3.5 h-3.5" /> Astuce : utilise la souris ou ton doigt pour tracer.
          </div>
        </div>
      </section>

      {/* Vidéo pédagogique — Trajectoire de sécurité (Sécurité Routière, RGPD-safe) */}
      <section className="mc-card p-4 sm:p-5" data-testid="trajectoires-video">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-md grid place-items-center bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]">
            <Compass className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Vidéo officielle
            </div>
            <h2 className="text-base sm:text-lg font-black tracking-tight">
              Trajectoire de sécurité — la méthode E-I-E en vidéo
            </h2>
          </div>
        </div>
        <SafeVideoEmbed
          videoId="5E-ITHJPj1k"
          title="Trajectoire de sécurité en virage — méthode E-I-E"
          channel="Sécurité routière"
          testid="trajectoires-video-embed"
        />
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          Vidéo pédagogique — comprendre en 3 minutes la trajectoire de sécurité et l'importance de la position sur la chaussée. Source&nbsp;: Sécurité Routière.
        </p>
      </section>

      <div className="space-y-4">
        {SITUATIONS.map((s, i) => {
          const Schema = s.Schema;
          return (
            <section key={s.id} className="mc-card p-4 sm:p-5" data-testid={`trajectoire-${s.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md grid place-items-center font-black text-white" style={{ background: 'hsl(var(--primary))' }}>{i + 1}</div>
                <h2 className="text-base sm:text-lg font-black tracking-tight">{s.title}</h2>
              </div>
              <div className="grid lg:grid-cols-[1fr_360px] gap-4">
                <div>
                  <TrajectoryCanvas
                    storageKey={`code2roues:trajectoire:${s.id}`}
                    backgroundSvg={<Schema />}
                    idealOverlay={<IdealOverlay d={s.idealPath} />}
                    height={360}
                    testid={`canvas-${s.id}`}
                    ariaLabel={`Zone de dessin pour ${s.title}`}
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">Points clés</div>
                    <ul className="space-y-1.5">
                      {s.points.map((p) => (
                        <li key={p.tag} className="flex items-start gap-2 text-sm">
                          <span className="w-6 h-6 rounded-full grid place-items-center font-black text-white text-[11px] flex-shrink-0 mt-0.5" style={{ background: p.color }}>{p.tag}</span>
                          <span className="leading-snug"><span className="font-bold">{p.label}</span> — <span className="text-foreground/80">{p.detail}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg p-3 border border-dashed" style={{ borderColor: 'hsl(var(--primary) / 0.4)', background: 'hsl(var(--primary) / 0.04)' }}>
                    <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">Éléments clés à respecter</div>
                    <ul className="space-y-1 text-xs leading-relaxed">
                      {s.keys.map((k, idx) => (<li key={idx} className="flex gap-1.5"><span className="text-[hsl(var(--primary))] flex-shrink-0">•</span><span>{k}</span></li>))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground italic mt-2 leading-relaxed">
        Source : technique de la trajectoire de sécurité (programme officiel REMC, arrêté du 23 avril 2012).
      </p>
    </div>
  );
}
